import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, ShieldCheck, Unlock, Lock, ChevronLeft, Filter, X as XIcon, ClipboardCheck, Check, CircleX } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateTime } from "../lib/dateFormat";

/**
 * Feb 2026 · Directive „PIN-Audit-Log".
 *
 * Zeigt eine chronologisch absteigend sortierte Tabelle aller PIN-
 * bezogenen Vorgänge:
 *   • unlock  — Admin hat sich mit der 4-stelligen PIN für die Bearbeitung
 *                einer Fremd-Concierge-Ansicht oder eines sensiblen
 *                Editors (z. B. Kundenprofil) freigeschaltet.
 *   • relock  — Admin hat wieder gesperrt (manuell oder automatisch beim
 *                Wechsel des impersonierten Concierges).
 *
 * Nachvollzieh-Regeln:
 *   - Fehlversuche werden bewusst NICHT geloggt (siehe `admin_pin.py`).
 *   - Kein serverseitiger Delete-Endpoint — das Log ist WORM.
 */
const PURPOSE_LABEL = {
  "other-concierge-edit": "Fremd-Concierge bearbeiten",
  "sensitive-edit": "Sensibler Editor",
  "project-edit": "Projekt-Änderung",
  "manual": "Manuell",
  "impersonation-changed": "Concierge-Wechsel",
  "role-switch": "Rollen-Wechsel (Alt-Verhalten)",
  "": "—",
};
const ACTION_LABEL = { unlock: "Freigeschaltet", relock: "Gesperrt" };

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return deDateTime(iso);
  } catch { return iso; }
}

export default function TeamPinAudit() {
  const [rows, setRows] = useState(null);
  const [filterActor, setFilterActor] = useState("all");
  const [filterTarget, setFilterTarget] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  // Feb 2026 · 9 Sep 2026 · User-Directive „PIN-Audit-Filter · Projekt-
  // Änderungen": eigenständiger Purpose-Filter, damit der Admin die neuen
  // `project-edit`-Freischaltungen isoliert prüfen kann, ohne durch
  // Fremd-Concierge- oder Sensitive-Editor-Zeilen scrollen zu müssen.
  const [filterPurpose, setFilterPurpose] = useState("all");
  // Feb 2026 · 11 Sep 2026 · User-Directive „PIN-Audit-Übersicht:
  // Kompakte Ansicht der letzten Team-PIN-Aktionen (jetzt inkl.
  // Concierges) mit Filter je Buchung." Zweiter Datenkanal aus dem
  // Backend (`/team/pin-audit/checklist-actions`) — zeigt die konkreten
  // Checklisten-Toggles, für die die PIN gerade eingegeben wurde.
  const [checklistRows, setChecklistRows] = useState(null);
  const [filterBookingId, setFilterBookingId] = useState("all");
  const [filterActorRole, setFilterActorRole] = useState("all");

  const load = async () => {
    try {
      const [audit, checklist] = await Promise.all([
        api.get("/team/pin-audit?limit=200"),
        api.get("/team/pin-audit/checklist-actions?limit=200"),
      ]);
      setRows(Array.isArray(audit.data) ? audit.data : []);
      setChecklistRows(Array.isArray(checklist.data) ? checklist.data : []);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
      setRows([]);
      setChecklistRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const actorOptions = useMemo(() => {
    const map = new Map();
    (rows || []).forEach((r) => {
      if (r.actor_id) map.set(r.actor_id, r.actor_name || r.actor_email || r.actor_id);
    });
    return Array.from(map.entries());
  }, [rows]);

  const targetOptions = useMemo(() => {
    const map = new Map();
    (rows || []).forEach((r) => {
      if (r.target_concierge_id) {
        map.set(r.target_concierge_id, r.target_concierge_name || r.target_concierge_id);
      }
    });
    return Array.from(map.entries());
  }, [rows]);

  const filtered = useMemo(() => {
    return (rows || []).filter((r) => {
      if (filterActor !== "all" && r.actor_id !== filterActor) return false;
      if (filterTarget !== "all" && r.target_concierge_id !== filterTarget) return false;
      if (filterAction !== "all" && r.action !== filterAction) return false;
      if (filterPurpose !== "all" && (r.purpose || "") !== filterPurpose) return false;
      return true;
    });
  }, [rows, filterActor, filterTarget, filterAction, filterPurpose]);

  // Feb 2026 · 11 Sep 2026 · Buchungs-Filter für die Team-Checklisten-
  // Aktionen. Der Admin schneidet die Checklisten-Historie auf EINE
  // Buchung ein, um zu sehen, wer wann welchen Punkt bewegt hat.
  const bookingOptions = useMemo(() => {
    const map = new Map();
    (checklistRows || []).forEach((r) => {
      if (!r.booking_id) return;
      const label = r.booking_property_name
        ? `${r.booking_property_name}${r.booking_from_date ? ` · ${r.booking_from_date}` : ""}`
        : r.booking_id.slice(0, 8);
      map.set(r.booking_id, label);
    });
    return Array.from(map.entries())
      .sort(([, a], [, b]) => a.localeCompare(b));
  }, [checklistRows]);

  const filteredChecklist = useMemo(() => {
    return (checklistRows || []).filter((r) => {
      if (filterBookingId !== "all" && r.booking_id !== filterBookingId) return false;
      if (filterActorRole !== "all" && r.actor_role !== filterActorRole) return false;
      return true;
    });
  }, [checklistRows, filterBookingId, filterActorRole]);

  const filterActive = filterActor !== "all" || filterTarget !== "all" || filterAction !== "all" || filterPurpose !== "all";
  const clearFilters = () => {
    setFilterActor("all"); setFilterTarget("all"); setFilterAction("all"); setFilterPurpose("all");
  };
  const clearChecklistFilters = () => {
    setFilterBookingId("all"); setFilterActorRole("all");
  };

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-10 py-8 md:py-12" data-testid="team-pin-audit">
      <div className="mb-6">
        <Link
          to="/team/settings/edit-pin"
          className="text-[11px] tracking-[0.15em] uppercase text-navy-dark/60 hover:text-navy inline-flex items-center gap-1 mb-3"
          data-testid="pin-audit-back-link"
        >
          <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
          Bearbeitungs-PIN
        </Link>
        <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>Sicherheit</div>
        <h1 className="font-display text-3xl md:text-4xl text-navy leading-tight">
          PIN-Audit-Log
        </h1>
        <p className="mt-3 text-[13.5px] text-navy-dark/70 leading-snug max-w-[720px]">
          Chronologische Übersicht aller PIN-Freischaltungen und Sperren. Jede
          Zeile zeigt, welcher Admin sich wann und für welchen Concierge in die
          Bearbeitung freigeschaltet hat. Fehlversuche werden bewusst nicht
          erfasst — die Kontrolle über wer welche PIN eingibt läuft weiterhin
          über die Admin-Anmeldung.
        </p>
      </div>

      {/* Filter-Zeile */}
      <div className="bg-white border border-navy/10 p-4 mb-5 flex items-center gap-3 flex-wrap" data-testid="pin-audit-filters">
        <Filter className="w-3.5 h-3.5 text-navy-dark/50" strokeWidth={1.6} />
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          data-testid="pin-audit-filter-action"
          className="text-[12.5px] border border-navy/20 px-2 py-1.5 bg-white focus:border-gold focus:outline-none"
        >
          <option value="all">Alle Aktionen</option>
          <option value="unlock">Nur Freischaltungen</option>
          <option value="relock">Nur Sperren</option>
        </select>
        <select
          value={filterActor}
          onChange={(e) => setFilterActor(e.target.value)}
          data-testid="pin-audit-filter-actor"
          className="text-[12.5px] border border-navy/20 px-2 py-1.5 bg-white focus:border-gold focus:outline-none"
        >
          <option value="all">Alle Admins</option>
          {actorOptions.map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
        <select
          value={filterTarget}
          onChange={(e) => setFilterTarget(e.target.value)}
          data-testid="pin-audit-filter-target"
          className="text-[12.5px] border border-navy/20 px-2 py-1.5 bg-white focus:border-gold focus:outline-none"
        >
          <option value="all">Alle Ziele</option>
          {targetOptions.map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
        <select
          value={filterPurpose}
          onChange={(e) => setFilterPurpose(e.target.value)}
          data-testid="pin-audit-filter-purpose"
          className="text-[12.5px] border border-navy/20 px-2 py-1.5 bg-white focus:border-gold focus:outline-none"
        >
          <option value="all">Alle Zwecke</option>
          <option value="project-edit">Projekt-Änderung</option>
          <option value="other-concierge-edit">Fremd-Concierge bearbeiten</option>
          <option value="sensitive-edit">Sensibler Editor</option>
          <option value="manual">Manuell</option>
          <option value="impersonation-changed">Concierge-Wechsel</option>
        </select>
        {filterActive && (
          <button
            type="button"
            onClick={clearFilters}
            data-testid="pin-audit-filter-clear"
            className="text-[11px] tracking-[0.15em] uppercase text-navy-dark/70 hover:text-navy inline-flex items-center gap-1 ml-auto"
          >
            <XIcon className="w-3 h-3" strokeWidth={2} />
            Zurücksetzen
          </button>
        )}
        <span className="ml-auto text-[11px] tracking-[0.15em] uppercase text-navy-dark/50" data-testid="pin-audit-count">
          {rows === null ? "…" : `${filtered.length} von ${rows.length}`}
        </span>
      </div>

      {/* Tabelle */}
      <div className="bg-white border border-navy/10 overflow-hidden">
        {rows === null ? (
          <div className="p-8 text-center text-[13px] text-navy-dark/50">Wird geladen …</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center" data-testid="pin-audit-empty">
            <ShieldCheck
              className="w-8 h-8 mx-auto mb-3 text-navy-dark/30"
              strokeWidth={1.4}
            />
            <div className="font-display text-xl text-navy mb-1">
              {rows.length === 0 ? "Noch keine Einträge" : "Kein Eintrag im Filter"}
            </div>
            <p className="text-[12.5px] text-navy-dark/60">
              {rows.length === 0
                ? "Sobald ein Admin die Bearbeitung eines Fremd-Concierges freischaltet, erscheint der Eintrag hier."
                : "Filter zurücksetzen, um wieder alle Einträge zu sehen."}
            </p>
          </div>
        ) : (
          <table className="w-full text-[12.5px]">
            <thead className="border-b border-navy/10">
              <tr className="text-left label-caps text-navy-dark/60">
                <th className="px-4 py-3">Zeit</th>
                <th className="px-4 py-3">Aktion</th>
                <th className="px-4 py-3">Admin</th>
                <th className="px-4 py-3">Concierge-Ansicht</th>
                <th className="px-4 py-3">Zweck</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const isUnlock = r.action === "unlock";
                const accent = isUnlock ? STATUS_COLORS.blue : STATUS_COLORS.green;
                return (
                  <tr
                    key={r.id}
                    className="border-b border-navy/5 last:border-b-0 hover:bg-navy/[0.02]"
                    data-testid={`pin-audit-row-${r.id}`}
                    data-action={r.action}
                  >
                    <td className="px-4 py-3 tabular-nums text-navy-dark whitespace-nowrap">
                      {fmtDateTime(r.at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10.5px] tracking-[0.15em] uppercase"
                        style={{ background: withAlpha(accent, 0.08), color: accent }}
                      >
                        {isUnlock ? <Unlock className="w-3 h-3" strokeWidth={1.8} /> : <Lock className="w-3 h-3" strokeWidth={1.8} />}
                        {ACTION_LABEL[r.action] || r.action}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-navy font-medium truncate max-w-[220px]">{r.actor_name || "—"}</div>
                      {r.actor_email && (
                        <div className="text-[11px] text-navy-dark/50 truncate max-w-[220px]">{r.actor_email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-navy truncate max-w-[220px]">
                        {r.target_concierge_name || <span className="text-navy-dark/40 italic">— (kein Ziel)</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-navy-dark/70">
                      {PURPOSE_LABEL[r.purpose || ""] || r.purpose}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-6 flex items-center gap-2 text-[11.5px] text-navy-dark/60">
        <KeyRound className="w-3.5 h-3.5" strokeWidth={1.6} />
        Das Log ist unveränderlich (WORM) — Zeilen können nicht gelöscht oder editiert werden.
      </div>

      {/* Feb 2026 · 11 Sep 2026 · Zweiter Datenkanal: Team-Checklisten-
          Aktionen (Admin + Concierge). Kompakte Tabelle direkt unter dem
          klassischen PIN-Log — beide Sichten teilen sich die Filter-
          Kacheln oben, haben aber eigene Booking- + Rollen-Filter. */}
      <ChecklistActionsSection
        rows={filteredChecklist}
        totalCount={checklistRows?.length ?? 0}
        loading={checklistRows === null}
        bookingOptions={bookingOptions}
        filterBookingId={filterBookingId}
        setFilterBookingId={setFilterBookingId}
        filterActorRole={filterActorRole}
        setFilterActorRole={setFilterActorRole}
        onClear={clearChecklistFilters}
      />
    </div>
  );
}

const CHECKLIST_ACTION_LABEL = {
  "service-toggle": "Service abgehakt",
  "checklist-toggle": "Checklisten-Punkt",
  "service-add": "Service hinzugefügt",
  "service-remove": "Service entfernt",
  "std-disable": "Standard deaktiviert",
  "std-restore": "Standard reaktiviert",
};
const CHECKLIST_ITEM_KIND_LABEL = {
  service: "Service",
  extra: "Extra",
  standard: "Standard",
  departure: "Abreise",
  arrival: "Anreise",
};
const CHECKLIST_ROLE_LABEL = { admin: "Admin", concierge: "Concierge" };

function ChecklistActionsSection({
  rows, totalCount, loading, bookingOptions,
  filterBookingId, setFilterBookingId,
  filterActorRole, setFilterActorRole,
  onClear,
}) {
  const filterActive = filterBookingId !== "all" || filterActorRole !== "all";
  return (
    <section className="mt-12" data-testid="pin-audit-checklist-section">
      <div className="mb-4">
        <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>
          Team-PIN-Aktionen
        </div>
        <h2 className="font-display text-2xl md:text-3xl text-navy leading-tight">
          Checklisten-Änderungen (Admin + Concierge)
        </h2>
        <p className="mt-2 text-[13px] text-navy-dark/70 leading-snug max-w-[720px]">
          Kompakte Übersicht aller Checklisten-Toggles, für die eine PIN
          eingegeben wurde. Seit dem 11 Sep 2026 sind auch Concierges
          PIN-pflichtig — die Filterzeile schneidet nach Buchung oder
          Rolle ein.
        </p>
      </div>

      <div
        className="bg-white border border-navy/10 p-4 mb-5 flex items-center gap-3 flex-wrap"
        data-testid="pin-audit-checklist-filters"
      >
        <Filter className="w-3.5 h-3.5 text-navy-dark/50" strokeWidth={1.6} />
        <select
          value={filterBookingId}
          onChange={(e) => setFilterBookingId(e.target.value)}
          data-testid="pin-audit-checklist-filter-booking"
          className="text-[12.5px] border border-navy/20 px-2 py-1.5 bg-white focus:border-gold focus:outline-none max-w-[280px] truncate"
        >
          <option value="all">Alle Buchungen</option>
          {bookingOptions.map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
        <select
          value={filterActorRole}
          onChange={(e) => setFilterActorRole(e.target.value)}
          data-testid="pin-audit-checklist-filter-role"
          className="text-[12.5px] border border-navy/20 px-2 py-1.5 bg-white focus:border-gold focus:outline-none"
        >
          <option value="all">Alle Rollen</option>
          <option value="admin">Nur Admin</option>
          <option value="concierge">Nur Concierge</option>
        </select>
        {filterActive && (
          <button
            type="button"
            onClick={onClear}
            data-testid="pin-audit-checklist-filter-clear"
            className="text-[11px] tracking-[0.15em] uppercase text-navy-dark/70 hover:text-navy inline-flex items-center gap-1 ml-auto"
          >
            <XIcon className="w-3 h-3" strokeWidth={2} />
            Zurücksetzen
          </button>
        )}
        <span
          className="ml-auto text-[11px] tracking-[0.15em] uppercase text-navy-dark/50"
          data-testid="pin-audit-checklist-count"
        >
          {loading ? "…" : `${rows.length} von ${totalCount}`}
        </span>
      </div>

      <div className="bg-white border border-navy/10 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[13px] text-navy-dark/50">Wird geladen …</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center" data-testid="pin-audit-checklist-empty">
            <ClipboardCheck
              className="w-8 h-8 mx-auto mb-3 text-navy-dark/30"
              strokeWidth={1.4}
            />
            <div className="font-display text-xl text-navy mb-1">
              {totalCount === 0 ? "Noch keine Checklisten-Aktion" : "Kein Eintrag im Filter"}
            </div>
            <p className="text-[12.5px] text-navy-dark/60">
              {totalCount === 0
                ? "Sobald ein Team-Mitglied einen Checklisten-Punkt mit PIN bewegt, erscheint der Eintrag hier."
                : "Filter zurücksetzen, um wieder alle Einträge zu sehen."}
            </p>
          </div>
        ) : (
          <table className="w-full text-[12.5px]">
            <thead className="border-b border-navy/10">
              <tr className="text-left label-caps text-navy-dark/60">
                <th className="px-4 py-3">Zeit</th>
                <th className="px-4 py-3">Rolle</th>
                <th className="px-4 py-3">Wer</th>
                <th className="px-4 py-3">Buchung</th>
                <th className="px-4 py-3">Aktion</th>
                <th className="px-4 py-3">Punkt</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const roleLabel = CHECKLIST_ROLE_LABEL[r.actor_role] || r.actor_role || "—";
                const roleColor = r.actor_role === "admin"
                  ? BRAND_COLORS.gold
                  : withAlpha(BRAND_COLORS.navy, 0.65);
                const actionLabel = CHECKLIST_ACTION_LABEL[r.action] || r.action;
                const kindLabel = CHECKLIST_ITEM_KIND_LABEL[r.item_kind] || r.item_kind || "";
                const bookingLabel = r.booking_property_name
                  ? `${r.booking_property_name}${r.booking_from_date ? ` · ${r.booking_from_date}` : ""}`
                  : (r.booking_id ? r.booking_id.slice(0, 8) + "…" : "—");
                const doneColor = r.done ? STATUS_COLORS.green : withAlpha(BRAND_COLORS.navy, 0.35);
                return (
                  <tr
                    key={r.id}
                    className="border-b border-navy/5 last:border-b-0 hover:bg-navy/[0.02]"
                    data-testid={`pin-audit-checklist-row-${r.id}`}
                    data-actor-role={r.actor_role || ""}
                    data-booking-id={r.booking_id || ""}
                  >
                    <td className="px-4 py-3 tabular-nums text-navy-dark whitespace-nowrap">
                      {fmtDateTime(r.at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className="inline-block px-2 py-0.5 text-[10.5px] tracking-[0.15em] uppercase"
                        style={{ background: withAlpha(roleColor, 0.1), color: roleColor }}
                      >
                        {roleLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-navy truncate max-w-[200px]">
                        {r.actor_name || "—"}
                      </div>
                      {r.actor_email && (
                        <div className="text-[11px] text-navy-dark/50 truncate max-w-[200px]">
                          {r.actor_email}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-navy truncate max-w-[240px]">{bookingLabel}</div>
                      {r.booking_customer_name && (
                        <div className="text-[11px] text-navy-dark/50 truncate max-w-[240px]">
                          {r.booking_customer_name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-navy-dark/80 whitespace-nowrap">
                      {actionLabel}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="w-4 h-4 flex items-center justify-center shrink-0 rounded-sm"
                          style={{ background: withAlpha(doneColor, 0.14), color: doneColor }}
                          aria-hidden
                        >
                          {r.done
                            ? <Check className="w-2.5 h-2.5" strokeWidth={3} />
                            : <CircleX className="w-2.5 h-2.5" strokeWidth={2.2} />}
                        </span>
                        <div className="min-w-0">
                          <div className="text-navy-dark/80 truncate max-w-[260px]">
                            {r.item_label || r.item_key || "—"}
                          </div>
                          {kindLabel && (
                            <div className="text-[10.5px] tracking-[0.14em] uppercase text-navy-dark/45">
                              {kindLabel}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

